import axios from 'axios';
import { format } from 'date-fns';

import { ApiResponse } from '../../models/api.response.model';
import { Error } from '../../models/error.model';
import { Message } from '../../models/message.model';
import { Company } from '../../models/company.model';
import { ProviderInvoice } from '../../models/provider.invoice.model';
import { CompanyProviderInvoices } from '../../models/company.provider.invoices.model';
import { authToken } from '../../scripts/authentication.script';
import { extractBase64 } from '../../scripts/extract.base.64.script';

export class ProviderInvoiceDA {
  static apiResponse: ApiResponse

  public static async getProviderInvoices(
    id_company: string,
    interval: string,
    start_date: string,
    end_date: string,
    contact_id: string,
    treasury_id: string,
    validity: string,
    status: string,
    payment: string,
    sort_field: string,
    sort_mode: string,
    group_by: string,
    page: number = 1,
    limit: number = 20,
    search: string = '',
    all: boolean = false
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let companyProviderInvoiceList: CompanyProviderInvoices | null = null
      let companyProviderInvoiceGroup: CompanyProviderInvoices | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/purchase.invoices.list.data.json').then(response => { companyProviderInvoiceList = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/purchase.invoices.group.data.json').then(response => { companyProviderInvoiceGroup = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (companyProviderInvoiceList!.id_company === id_company || companyProviderInvoiceGroup!.id_company === id_company) {
        if (group_by.length === 0) {
          companyProviderInvoiceList!.filter = { interval: interval, start_date: start_date, end_date: end_date, contact: contact_id, treasury: treasury_id, validity: validity, status: status, payment: payment, sort_field: sort_field, sort_mode: sort_mode, group_by: group_by }
        } else {
          companyProviderInvoiceGroup!.filter = { interval: interval, start_date: start_date, end_date: end_date, contact: contact_id, treasury: treasury_id, validity: validity, status: status, payment: payment, sort_field: sort_field, sort_mode: sort_mode, group_by: group_by }
        }

        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: (group_by.length === 0) ? companyProviderInvoiceList : companyProviderInvoiceGroup
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      const params = new URLSearchParams()
      if (interval) params.append('interval', interval)
      if (start_date) params.append('start_date', start_date)
      if (end_date) params.append('end_date', end_date)
      if (contact_id) params.append('id_contact', contact_id)
      if (treasury_id) params.append('treasury_account', treasury_id)
      if (validity === 'valid') params.append('validated', 'true')
      else if (validity === 'invalid') params.append('validated', 'false')
      if (status) params.append('status', status)
      if (payment) params.append('payment', payment)
      if (sort_field) params.append('sort_by', sort_field)
      if (sort_mode) params.append('sort_order', sort_mode)
      if (group_by) params.append('group_by', group_by)
      if (search) params.append('search', search)
      if (all) {
        params.append('all', 'true')
      } else {
        params.append('page', String(page))
        params.append('limit', String(limit))
      }
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/provider-invoices?${params.toString()}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.get(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async getProviderInvoice(
    id_company: string,
    id_provider_invoice: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let providerInvoice: ProviderInvoice | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/purchase.invoice.data.json').then(response => { providerInvoice = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (providerInvoice!.id === id_provider_invoice) {
        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: providerInvoice
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/provider-invoices/${id_provider_invoice}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.get(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response.status,
          message: error.response.statusText,
          data: error.response.data.error
        }
      })
    }

    return this.apiResponse
  }

  public static async postProviderInvoice(
    id_company: string,
    provider: any,
    concerning: string,
    date: string,
    currency: any,
    foreign_currency: any,
    requirements: { type: string, description: string, contents: { detail: string, reference: string, quantity: string, price_unit: string, tax_included: boolean, tax_code: string, tax_rate: number, unit: string }[] }[],
    vouchers: { name: string, file: any, id_document_inbox?: string }[],
    signatures: string[],
    company_detail: { uuid: string, name: string, logo: string, address: { street: string, number: string, additional: string, administrative_area: string, city: string, zip_code: string, country: string } },
    reference: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let company: Company | null = null
      let message: Message | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/company.data.json').then(response => { company = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/message.data.json').then(response => { message = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (company!.id === id_company) {
        this.apiResponse = {
          status: 201,
          message: 'Success',
          data: message
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/provider-invoices`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = new FormData();
      data.append("provider", JSON.stringify(provider))
      data.append("concerning", concerning)
      data.append('created_date', date)
      data.append('reference', reference)
      data.append('currency', JSON.stringify(currency))
      data.append('foreign_currency', foreign_currency ? JSON.stringify(foreign_currency) : '')
      data.append('company', JSON.stringify(company_detail))

      requirements.forEach((item) => {
        data.append('requirements', JSON.stringify(item))
      })

      signatures.forEach((item) => {
        data.append('signatures', item)
      })

      vouchers.forEach((item) => {
        if (item.file instanceof File) {
          data.append("vouchers", item.file, item.name)
        } else if (item.id_document_inbox) {
          data.append('voucher_document_ids', item.id_document_inbox)
        }
      })

      await axios.post(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response.status,
          message: error.response.statusText,
          data: error.response.data.error
        }
      })
    }

    return this.apiResponse
  }

  public static async postProviderInvoiceDraft(
    id_company: string,
    vouchers: { name: string, file: any, id_document_inbox?: string }[],
    company_detail: { uuid: string, name: string, logo: string, address: { street: string, number: string, additional: string, administrative_area: string, city: string, zip_code: string, country: string } }
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let company: Company | null = null
      let providerInvoice: ProviderInvoice | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/company.data.json').then(response => { company = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/purchase.invoice.draft.data.json').then(response => { providerInvoice = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (company!.id === id_company) {
        let listVouchers = await Promise.all(
          vouchers.map(async (item) => {
            let file = await extractBase64(item.file) as { base: string }
            return {
              id: `voucher${(Math.floor(Math.random() * 1000)).toString()}`,
              name: item.name,
              size: item.name.length * 100,
              created_at: `${format((new Date()), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)}`,
              file: file.base
            }
          })
        )

        providerInvoice!.vouchers = listVouchers

        this.apiResponse = {
          status: 201,
          message: 'Success',
          data: providerInvoice
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/provider-invoices/draft`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = new FormData();
      data.append('company', JSON.stringify(company_detail))

      vouchers.forEach((item) => {
        if (item.file instanceof File) {
          data.append("vouchers", item.file, item.name)
        } else if (item.id_document_inbox) {
          data.append('voucher_document_ids', item.id_document_inbox)
        }
      })

      await axios.post(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response.status,
          message: error.response.statusText,
          data: error.response.data.error
        }
      })
    }

    return this.apiResponse
  }

  public static async postProviderInvoiceReference(
    id_company: string,
    id_provider_order: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let providerInvoice: ProviderInvoice | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/purchase.invoice.data.json').then(response => { providerInvoice = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (providerInvoice!.provider_order.id === id_provider_order) {
        this.apiResponse = {
          status: 201,
          message: 'Success',
          data: providerInvoice
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/provider-invoices/${id_provider_order}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.post(url, null, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response.status,
          message: error.response.statusText,
          data: error.response.data.error
        }
      })
    }

    return this.apiResponse
  }

  public static async putProviderInvoice(
    id_company: string,
    id_provider_invoice: string,
    provider: any,
    concerning: string,
    date: string,
    currency: any,
    foreign_currency: any,
    requirements: { type: string, description: string, contents: { detail: string, reference: string, quantity: string, price_unit: string, tax_included: boolean, tax_code: string, tax_rate: number, unit: string }[] }[],
    vouchers: { name: string, file: any, id_document_inbox?: string }[],
    signatures: string[]
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let providerInvoice: ProviderInvoice | null = null
      let message: Message | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/purchase.invoice.data.json').then(response => { providerInvoice = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/message.data.json').then(response => { message = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (providerInvoice!.id === id_provider_invoice) {
        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: message
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/provider-invoices/${id_provider_invoice}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = new FormData()
      data.append("provider", JSON.stringify(provider))
      data.append("concerning", concerning)
      data.append('created_date', date)
      data.append('currency', JSON.stringify(currency))
      data.append('foreign_currency', foreign_currency ? JSON.stringify(foreign_currency) : '')

      requirements.forEach((item) => {
        data.append('requirements', JSON.stringify(item))
      })

      signatures.forEach((item) => {
        data.append('signatures', item)
      })

      vouchers.forEach((item) => {
        if (item.file instanceof File) {
          data.append("vouchers", item.file, item.name)
        } else if (item.id_document_inbox) {
          data.append('voucher_document_ids', item.id_document_inbox)
        }
      })

      await axios.put(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response.status,
          message: error.response.statusText,
          data: error.response.data.error
        }
      })
    }

    return this.apiResponse
  }

  public static async patchProviderInvoiceStorageOperations(
    id_company: string,
    id_provider_invoice: string,
    id_storage_operation: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      this.apiResponse = { status: 200, message: 'Success', data: null }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/provider-invoices/${id_provider_invoice}/storage-operations`
      let config: any = { headers: { Authorization: authToken() } }
      let data: any = { id_storage_operation: id_storage_operation }

      await axios.patch(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Unknown error',
          data: error.response?.data?.error ?? error.message
        }
      })
    }

    return this.apiResponse
  }

  public static async putProviderInvoiceStatus(
    id_company: string,
    id_invoice: string,
    amount: number | null,
    _company_tax: { subject_tax: boolean, tax_method: string, tax_declaration: string, tax_period: string, tax_statement: string, language: string } | null,
    content_accounts: { id_content: string, id_account_credit: string, id_account_debit: string }[] | null,
    effective_accounts: { id_content: string, id_account_tax: string }[] | null,
    foreign_currency?: any
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let providerInvoice: ProviderInvoice | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/purchase.invoice.data.json').then(response => { providerInvoice = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (providerInvoice!.id === id_invoice) {
        providerInvoice!.validated = (providerInvoice!.validated) ? false : true
        providerInvoice!.status = (providerInvoice!.validated) ? 'approved' : 'in-progress'

        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: providerInvoice
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/provider-invoices/${id_invoice}/status`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = {
        amount: amount,
        content_accounts: content_accounts,
        effective_accounts: effective_accounts,
        foreign_currency: foreign_currency ?? null
      }

      await axios.put(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response.status,
          message: error.response.statusText,
          data: error.response.data.error
        }
      })
    }

    return this.apiResponse
  }

  public static async attachVoucher(id_company: string, id_provider_invoice: string, body: { id_document_inbox: string, role?: string }): Promise<any> {
    try {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/provider-invoices/${id_provider_invoice}/vouchers`
      let config: any = { headers: { Authorization: authToken() } }
      const response = await axios.post(url, body, config)
      return { status: response.status, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, data: error.response?.data ?? {} }
    }
  }

  public static async uploadVoucher(id_company: string, id_provider_invoice: string, file: File, role?: string): Promise<any> {
    try {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/provider-invoices/${id_provider_invoice}/vouchers/upload`
      const formData = new FormData()
      formData.append('file', file)
      formData.append('source', 'web')
      if (role) formData.append('role', role)
      let config: any = { headers: { Authorization: authToken(), 'Content-Type': 'multipart/form-data' } }
      const response = await axios.post(url, formData, config)
      return { status: response.status, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, data: error.response?.data ?? {} }
    }
  }

  public static async deleteProviderInvoice(id_company: string, id_provider_invoice: string): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/provider-invoices/${id_provider_invoice}`
    let config: any = { headers: { Authorization: authToken() } }

    try {
      const response = await axios.delete(url, config)
      return { status: response.status, message: response.statusText, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, message: error.response?.statusText ?? 'Error', data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message } }
    }
  }

  public static async revertToDraft(id_company: string, id_provider_invoice: string): Promise<ApiResponse> {
    try {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/provider-invoices/${id_provider_invoice}/revert-draft`
      let config: any = { headers: { Authorization: authToken() } }
      const response = await axios.put(url, {}, config)
      return { status: response.status, message: response.statusText, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, message: error.response?.statusText ?? '', data: error.response?.data ?? {} }
    }
  }

  public static async acceptProviderInvoice(
    id_company: string,
    id_provider_invoice: string
  ): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/provider-invoices/${id_provider_invoice}/accept`
    let config: any = { headers: { Authorization: authToken() } }

    await axios.post(url, {}, config).then(response => {
      this.apiResponse = {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    }).catch(error => {
      this.apiResponse = {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    })

    return this.apiResponse
  }

  public static async declineProviderInvoice(
    id_company: string,
    id_provider_invoice: string,
    reason: string = ''
  ): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/provider-invoices/${id_provider_invoice}/decline`
    let config: any = { headers: { Authorization: authToken() } }

    await axios.post(url, { reason }, config).then(response => {
      this.apiResponse = {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    }).catch(error => {
      this.apiResponse = {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    })

    return this.apiResponse
  }

  public static async deleteVoucher(id_company: string, id_provider_invoice: string, index: number): Promise<any> {
    try {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/provider-invoices/${id_provider_invoice}/vouchers/${index}`
      let config: any = { headers: { Authorization: authToken() } }
      const response = await axios.delete(url, config)
      return { status: response.status, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, data: error.response?.data ?? {} }
    }
  }

  public static async patchAccounts(id_company: string, id_provider_invoice: string, content_accounts: {id_content: string, id_account_debit: string, id_account_credit: string}[], effective_accounts?: {id_content: string, id_account_tax: string}[] | null, company?: {subject_tax: boolean, tax_method: string, tax_declaration: string, tax_period: string, tax_statement: string, language: string} | null): Promise<ApiResponse> {
    try {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/provider-invoices/${id_provider_invoice}/accounts`
      let config: any = { headers: { Authorization: authToken() } }
      const response = await axios.patch(url, { content_accounts, effective_accounts: effective_accounts ?? undefined, company: company ?? undefined }, config)
      return { status: response.status, message: response.statusText, data: response.data?.data ?? response.data }
    } catch (error: any) {
      return { status: error.response?.status ?? 500, message: error.response?.statusText ?? '', data: error.response?.data ?? {} }
    }
  }
}
